import axios from 'axios'
import { localizationConstants } from '../resources/theme/localizationConstants'
import { apiHeaders } from './apiConstants'
import { updateIsLoading, updateToastData } from '../toast/toastSlice'
import {
	changeRoute,
	getUserFromLocalStorage,
	removeUserFromLocalStorage,
} from './utils'
import { routePaths } from '../routes/routeConstants'
import { toUserFriendlyError } from './apiErrorMessages'
import { captchaService } from '../features/securityVerification/captchaService'

/** Lazy load avoids the store <-> dashboard thunk cycle leaving a reducer undefined. */
function getStore() {
	return require('../store').store
}

/** Skip the bar on very fast requests so the page does not flicker. */
const LOADER_DELAY_MS = 120

let activeLoaderRequests = 0
let loaderDelayTimer = null
let loaderVisible = false

function scheduleLoader() {
	activeLoaderRequests += 1
	if (activeLoaderRequests === 1 && !loaderDelayTimer && !loaderVisible) {
		loaderDelayTimer = setTimeout(() => {
			loaderDelayTimer = null
			if (activeLoaderRequests > 0) {
				loaderVisible = true
				getStore().dispatch(updateIsLoading(true))
			}
		}, LOADER_DELAY_MS)
	}
}

function releaseLoader() {
	activeLoaderRequests = Math.max(0, activeLoaderRequests - 1)
	if (activeLoaderRequests === 0) {
		if (loaderDelayTimer) {
			clearTimeout(loaderDelayTimer)
			loaderDelayTimer = null
		}
		if (loaderVisible) {
			loaderVisible = false
			getStore().dispatch(updateIsLoading(false))
		}
	}
}

function restoreSkipHeaders(config) {
	if (!config?.headers) return
	if (config._skipLoader) {
		config.headers[apiHeaders.preventDefaultLoader] = true
	}
	if (config._skipToast) {
		config.headers[apiHeaders.preventDefaultToast] = true
	}
}

function getErrorMessage(error) {
	return error?.code === 'ERR_NETWORK'
		? localizationConstants.contactAdminSomethingWrong
		: (error?.response?.data?.error_message ??
			error?.response?.data?.error ??
			error?.response?.data?.errors ??
			error?.message ??
			localizationConstants.contactAdminSomethingWrong)
}

function showErrorToast(error, errorMessage) {
	if (error?.config?.headers?.[apiHeaders.preventDefaultToast]) return
	if (!errorMessage) return

	const errorKey =
		typeof errorMessage === 'string'
			? []
			: Object.keys(errorMessage[0]?.errors)[0]
	let subTitle = ''
	if (error?.response?.data?.fileContainsError) {
		subTitle = error?.response?.data?.message
	} else {
		subTitle =
			typeof errorMessage === 'string'
				? toUserFriendlyError(
						errorMessage,
						localizationConstants.contactAdminSomethingWrong,
					)
				: toUserFriendlyError(
						errorMessage[0]?.errors[errorKey],
						localizationConstants.contactAdminSomethingWrong,
					)
	}
	getStore().dispatch(
		updateToastData({
			showToast: true,
			title: '',
			subTitle,
			isSuccess: false,
			multipleError: Array.isArray(errorMessage) ? errorMessage : [],
			direction: error?.response?.data?.fileContainsError
				? 'left'
				: 'right',
		}),
	)
}

function finalizeUnauthorizedSession() {
	captchaService.resetSession()
	removeUserFromLocalStorage()
	changeRoute(routePaths.login)
}

function applyDefaultErrorHandling(error, errorMessage) {
	if (error?.config) restoreSkipHeaders(error.config)
	const skipToast =
		Boolean(error?.config?._skipToast) ||
		Boolean(error?.config?.headers?.[apiHeaders.preventDefaultToast])
	if (error?.config && !skipToast) {
		showErrorToast(error, errorMessage)
	}

	if (error?.code === 'ECONNABORTED') {
		console.log('Request timed out')
	}

	if (error?.code === 'ERR_NETWORK') {
		console.log('network error')
	}

	if (error?.response?.status === 404) {
		console.log('not found')
	}
	if (error?.response?.status === 400) {
		console.log('bad request')
	}
	if (error?.response?.status === 401) {
		finalizeUnauthorizedSession()
	}

	delete error?.config?.headers?.[apiHeaders.preventDefaultLoader]
	delete error?.config?.headers?.[apiHeaders.preventDefaultToast]
}

async function retryAfterSecurityChallenge(error) {
	if (!captchaService.shouldChallenge(error)) return null

	const verified = await captchaService.ensureHumanVerified()
	if (!verified) return null

	const config = error.config
	config._securityRetried = true
	restoreSkipHeaders(config)
	return myPeeguAxios(config)
}

const myPeeguAxios = axios.create({
	baseURL: `${process.env.REACT_APP_BASE_URL}`,
	headers: {
		Accept: apiHeaders.acceptType,
		'ngrok-skip-browser-warning': 'true',
		[apiHeaders.deviceType]: apiHeaders.web,
	},
	timeout: 60000,
})

myPeeguAxios.interceptors.request.use(
	(config) => {
		const user = getUserFromLocalStorage()

		if (user) {
			config.headers[apiHeaders.authToken] = user.authToken
		}

		const skipLoader = Boolean(config.headers[apiHeaders.preventDefaultLoader])
		const skipToast = Boolean(config.headers[apiHeaders.preventDefaultToast])
		config._skipLoader = skipLoader
		config._skipToast = skipToast
		config._useDefaultLoader = !skipLoader
		config._skipSecurityChallenge = Boolean(
			config.headers[apiHeaders.skipSecurityChallenge] ||
				config._skipSecurityChallenge,
		)
		if (config._useDefaultLoader) {
			scheduleLoader()
		}

		delete config.headers[apiHeaders.preventDefaultLoader]
		delete config.headers[apiHeaders.preventDefaultToast]
		delete config.headers[apiHeaders.skipSecurityChallenge]

		return config
	},
	(error) => Promise.reject(error),
)

myPeeguAxios.interceptors.response.use(
	(response) => {
		if (!response.config.headers[apiHeaders.preventDefaultToast]) {
			const toastMessage =
				typeof response?.data?.message === 'string'
					? response.data.message
					: typeof response?.data?.message?.message === 'string'
						? response.data.message.message
						: null
			if (toastMessage) {
				getStore().dispatch(
					updateToastData({
						showToast: true,
						title: '',
						subTitle: toastMessage,
						isSuccess: true,
						direction: response?.response?.data?.fileContainsError
							? 'left'
							: 'right',
					}),
				)
			}
		}

		if (response.config._useDefaultLoader) {
			releaseLoader()
		}

		delete response?.config?.headers[apiHeaders.preventDefaultLoader]
		delete response?.config?.headers[apiHeaders.preventDefaultToast]

		return response
	},
	(error) => {
		const errorMessage = getErrorMessage(error)
		error.errorMessage = errorMessage

		if (error?.config?._useDefaultLoader) {
			releaseLoader()
		}

		return retryAfterSecurityChallenge(error).then((retried) => {
			if (retried) return retried
			applyDefaultErrorHandling(error, errorMessage)
			return Promise.reject(error)
		})
	},
)

export default myPeeguAxios
